import {
  countManuscriptWords,
} from "./childrensBookManuscriptCapacity";

import {
  plainTextFromManuscriptEntry,
} from "./childrensBookManuscriptContent";

import {
  buildChildrensBookStructuredManuscriptDivision,
  suggestChildrensBookStructuredManuscriptDivision,
} from "./childrensBookManuscriptContentDivision";

const DIVISION_UNIT_TYPES =
  new Set([
    "single_page",
  ]);

export const MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS =
  250;

export const MANUSCRIPT_PAGE_TARGET_WORDS =
  300;

export const MANUSCRIPT_PAGE_MAXIMUM_WORDS =
  350;

function clean(value) {
  return String(value ?? "").trim();
}

function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}

function hasOwn(
  value,
  key
) {
  return Boolean(
    value &&
    typeof value === "object" &&
    !Array.isArray(value) &&
    Object.prototype
      .hasOwnProperty.call(
        value,
        key
      )
  );
}

function comparableText(value) {
  return clean(value).replace(
    /\s+/g,
    " "
  );
}

function pageSpan(unit = {}) {
  return unit.unitType ===
    "double_page_spread"
    ? 2
    : 1;
}

function uniquePartId(
  sourceUnitId,
  partNumber,
  usedIds
) {
  const base =
    `${sourceUnitId}-part-${partNumber}`;

  let candidate = base;
  let suffix = 2;

  while (
    usedIds.has(candidate)
  ) {
    candidate =
      `${base}-${suffix}`;

    suffix += 1;
  }

  usedIds.add(candidate);

  return candidate;
}

function divisionWorkingTitle(
  sourceTitle,
  index
) {
  return index === 0
    ? clean(sourceTitle)
    : "";
}

function writerFacingDestination(
  unitType,
  startPage
) {
  if (
    unitType ===
    "double_page_spread"
  ) {
    return {
      pageNumber:
        `${startPage}–${startPage + 1}`,

      pageLabel:
        `Pages ${startPage}–${startPage + 1}`,

      destinationLabel:
        `Two-page Spread — Pages ${startPage}–${startPage + 1}`,
    };
  }

  return {
    pageNumber:
      String(startPage),

    pageLabel:
      `Page ${startPage}`,

    destinationLabel:
      `Single Page — Page ${startPage}`,
  };
}

function renumberBookMapUnits(
  units
) {
  let nextPage = 1;

  return safeArray(units).map(
    (unit) => {
      const destination =
        writerFacingDestination(
          clean(unit?.unitType),
          nextPage
        );

      const renumberedUnit = {
        ...unit,

        pageNumber:
          destination.pageNumber,
      };

      nextPage +=
        pageSpan(
          renumberedUnit
        );

      return renumberedUnit;
    }
  );
}

function activeIllustrationForUnit(
  assets,
  unitId
) {
  return safeArray(assets).find(
    (asset) =>
      asset?.assetType ===
        "illustration" &&
      asset?.purpose ===
        "book_map_illustration" &&
      asset?.status ===
        "active" &&
      clean(
        asset?.bookMapUnitId
      ) === unitId
  ) || null;
}

function sourceIllustrationContext({
  unit,
  illustrationEntries,
  childrensBookAssets,
  sourceUnitId,
}) {
  const illustrationEntry =
    illustrationEntries.find(
      (entry) =>
        clean(entry?.unitId) ===
        sourceUnitId
    ) || null;

  const activeAsset =
    activeIllustrationForUnit(
      childrensBookAssets,
      sourceUnitId
    );

  const unitIllustrationPlan =
    clean(
      unit?.illustrationPlan
    );

  const hasPlanningContext =
    Boolean(
      illustrationEntry ||
      activeAsset ||
      (
        unitIllustrationPlan &&
        unitIllustrationPlan !==
          "none"
      ) ||
      clean(
        unit?.illustrationNote
      ) ||
      clean(
        unit?.layoutIntention
      )
    );

  return {
    hasPlanningContext,
    illustrationEntry,
    activeAsset,
    unitIllustrationPlan:
      unitIllustrationPlan ||
      "none",
  };
}

function failure(
  code,
  error
) {
  return {
    ok: false,
    readyToApply: false,
    code,
    error,
  };
}

export function buildChildrensBookManuscriptUnitDivisionProposal({
  projectPlan = {},
  sourceUnitId = "",
  segments = [],
  unitTypes = [],
  illustrationSegmentIndex,
  childrensBookAssets = [],
} = {}) {
  const normalizedSourceUnitId =
    clean(sourceUnitId);

  const units = safeArray(
    projectPlan.bookMap?.units
  );

  const manuscriptEntries =
    safeArray(
      projectPlan.manuscriptPlan
        ?.entries
    );

  const illustrationEntries =
    safeArray(
      projectPlan.illustrationPlan
        ?.entries
    );

  const sourceUnitIndex =
    units.findIndex(
      (unit) =>
        clean(unit?.id) ===
        normalizedSourceUnitId
    );

  if (
    !normalizedSourceUnitId ||
    sourceUnitIndex < 0
  ) {
    return failure(
      "source_unit_not_found",
      "The page or spread to divide could not be found."
    );
  }

  const sourceUnit =
    units[sourceUnitIndex];

  const matchingEntries =
    manuscriptEntries.filter(
      (entry) =>
        clean(entry?.unitId) ===
        normalizedSourceUnitId
    );

  if (
    matchingEntries.length !== 1
  ) {
    return failure(
      "source_manuscript_not_found",
      "Exactly one manuscript entry is required for the page or spread being divided."
    );
  }

  const sourceEntry =
    matchingEntries[0];

  const structuredSource =
    hasOwn(
      sourceEntry,
      "manuscriptContent"
    );

  const sourceText =
    plainTextFromManuscriptEntry(
      sourceEntry
    );

  if (
    !structuredSource &&
    !clean(sourceText)
  ) {
    return failure(
      "source_manuscript_empty",
      "The manuscript unit has no text to divide."
    );
  }

  if (
    !Array.isArray(segments) ||
    segments.length < 2
  ) {
    return failure(
      "insufficient_segments",
      "Divide the manuscript into at least two parts."
    );
  }

  const reviewedSegments =
    segments.map(clean);

  if (
    reviewedSegments.some(
      (segment) => !segment
    )
  ) {
    return failure(
      "empty_segment",
      "Every proposed physical page must contain manuscript text."
    );
  }

  if (
    !Array.isArray(unitTypes) ||
    unitTypes.length !==
      reviewedSegments.length
  ) {
    return failure(
      "unit_type_count_mismatch",
      "Provide one physical-page type for every proposed page."
    );
  }

  if (
    unitTypes.some(
      (unitType) =>
        !DIVISION_UNIT_TYPES.has(
          clean(unitType)
        )
    )
  ) {
    return failure(
      "invalid_unit_type",
      "Division may create only physical Single Pages."
    );
  }

  const structuredDivision =
    structuredSource
      ? buildChildrensBookStructuredManuscriptDivision({
          manuscriptContent:
            sourceEntry.manuscriptContent,
          segments:
            reviewedSegments,
          wordLimit:
            MANUSCRIPT_PAGE_MAXIMUM_WORDS,
        })
      : null;

  if (
    structuredSource &&
    !structuredDivision.ok
  ) {
    return failure(
      structuredDivision.code,
      structuredDivision.error
    );
  }

  if (!structuredSource) {
    const reconstructedText =
      comparableText(
        reviewedSegments.join(
          " "
        )
      );

    if (
      reconstructedText !==
      comparableText(sourceText)
    ) {
      return failure(
        "manuscript_text_changed",
        "The proposed division does not preserve the complete manuscript text in its original order."
      );
    }
  }

  const proposalSegments =
    structuredSource
      ? structuredDivision.segments
      : reviewedSegments.map(
          (manuscriptText) => ({
            manuscriptText,
            manuscriptContent: null,
          })
        );

  const normalizedSegments =
    proposalSegments.map(
      (segment) =>
        segment.manuscriptText
    );

  const illustrationContext =
    sourceIllustrationContext({
      unit: sourceUnit,
      illustrationEntries,
      childrensBookAssets,
      sourceUnitId:
        normalizedSourceUnitId,
    });

  const illustrationIndexProvided =
    Number.isInteger(
      illustrationSegmentIndex
    );

  if (
    illustrationIndexProvided &&
    (
      illustrationSegmentIndex < 0 ||
      illustrationSegmentIndex >=
        reviewedSegments.length
    )
  ) {
    return failure(
      "invalid_illustration_segment",
      "Choose a valid resulting physical page for the original illustration connection."
    );
  }

  const effectiveIllustrationIndex =
    illustrationIndexProvided
      ? illustrationSegmentIndex
      : null;

  const sourceIdentitySegmentIndex =
    illustrationContext
      .activeAsset &&
    illustrationIndexProvided
      ? effectiveIllustrationIndex
      : 0;

  const sourceMetadataSegmentIndex =
    0;

  const usedIds =
    new Set(
      units
        .map(
          (unit) =>
            clean(unit?.id)
        )
        .filter(
          (unitId) =>
            unitId &&
            unitId !==
              normalizedSourceUnitId
        )
    );

  const pagesBeforeSource =
    units
      .slice(
        0,
        sourceUnitIndex
      )
      .reduce(
        (
          total,
          unit
        ) =>
          total +
          pageSpan(unit),
        0
      );

  let nextPage =
    pagesBeforeSource + 1;

  const proposedSegments =
    normalizedSegments.map(
      (
        manuscriptText,
        index
      ) => {
        const unitType =
          clean(
            unitTypes[index]
          );

        const retainsSourceIdentity =
          index ===
          sourceIdentitySegmentIndex;

        const retainsSourceMetadata =
          index ===
          sourceMetadataSegmentIndex;

        const keepsIllustration =
          index ===
          effectiveIllustrationIndex;

        const unitId =
          retainsSourceIdentity
            ? normalizedSourceUnitId
            : uniquePartId(
                normalizedSourceUnitId,
                index + 1,
                usedIds
              );

        if (
          retainsSourceIdentity
        ) {
          usedIds.add(unitId);
        }

        const destination =
          writerFacingDestination(
            unitType,
            nextPage
          );

        const proposedUnit = {
          id: unitId,
          unitType,

          pageNumber:
            destination.pageNumber,

          workingTitle:
            divisionWorkingTitle(
              sourceUnit.workingTitle,
              index
            ),

          purpose:
            retainsSourceMetadata
              ? clean(
                  sourceUnit.purpose
                )
              : "",

          textAmount:
            retainsSourceMetadata
              ? clean(
                  sourceUnit.textAmount
                )
              : "",

          illustrationPlan:
            keepsIllustration
              ? (
                  clean(
                    sourceUnit
                      .illustrationPlan
                  ) || "none"
                )
              : "none",

          illustrationNote:
            keepsIllustration
              ? clean(
                  sourceUnit
                    .illustrationNote
                )
              : "",

          layoutIntention:
            keepsIllustration
              ? clean(
                  sourceUnit
                    .layoutIntention
                )
              : "",

          status:
            retainsSourceMetadata
              ? clean(
                  sourceUnit.status
                )
              : "",
        };

        const proposedEntry = {
          unitId,
          manuscriptText,

          ...(
            structuredSource
              ? {
                  manuscriptContent:
                    proposalSegments[
                      index
                    ].manuscriptContent,
                }
              : {}
          ),

          authorNote:
            retainsSourceMetadata
              ? clean(
                  sourceEntry
                    .authorNote
                )
              : "",

          status:
            clean(
              sourceEntry.status
            ),
        };

        const pageWordCount =
          countManuscriptWords(
            manuscriptText
          );

        const capacity = {
          wordLimit:
            MANUSCRIPT_PAGE_MAXIMUM_WORDS,

          oversized:
            pageWordCount >
            MANUSCRIPT_PAGE_MAXIMUM_WORDS,
        };

        const result = {
          index,
          unitId,
          unitType,
          retainsSourceIdentity,
          retainsSourceMetadata,
          keepsIllustration,

          pageNumber:
            destination.pageNumber,

          pageStart:
            nextPage,

          pageEnd:
            nextPage +
            pageSpan(
              proposedUnit
            ) - 1,

          pageLabel:
            destination.pageLabel,

          destinationLabel:
            destination
              .destinationLabel,

          wordCount:
            pageWordCount,

          wordLimit:
            capacity.wordLimit,

          preferredMinimum:
            unitType ===
            "single_page"
              ? MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS
              : null,

          preferredTarget:
            unitType ===
            "single_page"
              ? MANUSCRIPT_PAGE_TARGET_WORDS
              : null,

          belowPreferredMinimum:
            unitType ===
              "single_page" &&
            normalizedSegments.length >
              1 &&
            pageWordCount <
              MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS,

          oversized:
            capacity.oversized,

          unit: proposedUnit,

          manuscriptEntry:
            proposedEntry,
        };

        nextPage +=
          pageSpan(
            proposedUnit
          );

        return result;
      }
    );

  const candidateUnits = [
    ...units.slice(
      0,
      sourceUnitIndex
    ),

    ...proposedSegments.map(
      (segment) =>
        segment.unit
    ),

    ...units.slice(
      sourceUnitIndex + 1
    ),
  ];

  const nextUnits =
    renumberBookMapUnits(
      candidateUnits
    );

  const sourceEntryIndex =
    manuscriptEntries.findIndex(
      (entry) =>
        clean(entry?.unitId) ===
        normalizedSourceUnitId
    );

  const nextManuscriptEntries = [
    ...manuscriptEntries.slice(
      0,
      sourceEntryIndex
    ),

    ...proposedSegments.map(
      (segment) =>
        segment.manuscriptEntry
    ),

    ...manuscriptEntries.slice(
      sourceEntryIndex + 1
    ),
  ];

  const illustrationDestination =
    illustrationIndexProvided
      ? {
          segmentIndex:
            effectiveIllustrationIndex,

          unitId:
            proposedSegments[
              effectiveIllustrationIndex
            ]?.unitId || "",
        }
      : null;

  const nextIllustrationEntries =
    illustrationDestination
      ? illustrationEntries.map(
          (entry) =>
            clean(entry?.unitId) ===
            normalizedSourceUnitId
              ? {
                  ...entry,

                  unitId:
                    illustrationDestination
                      .unitId,
                }
              : entry
        )
      : illustrationEntries;

  const oversizedSegments =
    proposedSegments.filter(
      (segment) =>
        segment.oversized
    );

  const requirements = [];

  if (
    illustrationContext
      .hasPlanningContext &&
    !illustrationIndexProvided
  ) {
    requirements.push({
      type:
        "illustration_assignment",

      message:
        "Choose which resulting physical page keeps the original illustration and illustration planning.",

      hasActiveAsset:
        Boolean(
          illustrationContext
            .activeAsset
        ),

      hasIllustrationPlanEntry:
        Boolean(
          illustrationContext
            .illustrationEntry
        ),

      options:
        proposedSegments.map(
          (segment) => ({
            segmentIndex:
              segment.index,

            unitId:
              segment.unitId,

            destinationLabel:
              segment
                .destinationLabel,

            wordCount:
              segment.wordCount,
          })
        ),
    });
  }

  oversizedSegments.forEach(
    (segment) => {
      requirements.push({
        type:
          "segment_capacity",

        segmentIndex:
          segment.index,

        unitId:
          segment.unitId,

        destinationLabel:
          segment
            .destinationLabel,

        wordCount:
          segment.wordCount,

        wordLimit:
          segment.wordLimit,

        message:
          `${segment.destinationLabel} contains ${segment.wordCount} words and exceeds its ${segment.wordLimit}-word review limit.`,
      });
    }
  );

  const readyToApply =
    oversizedSegments.length === 0 &&
    (
      !illustrationContext
        .hasPlanningContext ||
      illustrationIndexProvided
    );

  return {
    ok: true,
    readyToApply,

    source: {
      unitId:
        normalizedSourceUnitId,

      unitIndex:
        sourceUnitIndex,

      wordCount:
        countManuscriptWords(
          sourceText
        ),

      contentMode:
        structuredSource
          ? "structured"
          : "legacy_text",

      unit: sourceUnit,
      manuscriptEntry:
        sourceEntry,
    },

    summary: {
      segmentCount:
        proposedSegments.length,

      estimatedPageCount:
        proposedSegments.reduce(
          (
            total,
            segment
          ) =>
            total +
            pageSpan(
              segment.unit
            ),
          0
        ),

      sourceIdentitySegmentIndex,

      illustrationSegmentIndex:
        effectiveIllustrationIndex,

      illustrationSegmentConfirmed:
        !illustrationContext
          .hasPlanningContext ||
        illustrationIndexProvided,

      manuscriptTextPreserved:
        true,

      manuscriptStructurePreserved:
        structuredSource
          ? true
          : null,

      formattingPreserved:
        structuredSource
          ? true
          : null,

      pageBreaksPreserved:
        structuredSource
          ? true
          : null,

      sourceContentMode:
        structuredSource
          ? "structured"
          : "legacy_text",

      oversizedSegmentCount:
        oversizedSegments.length,

      belowPreferredMinimumPageCount:
        proposedSegments.filter(
          (segment) =>
            segment
              .belowPreferredMinimum
        ).length,

      physicalPageModel:
        proposedSegments.every(
          (segment) =>
            segment.unitType ===
            "single_page"
        ),
    },

    illustrationContext,
    illustrationDestination,
    requirements,
    proposedSegments,

    nextBookMap: {
      ...(
        projectPlan.bookMap ||
        {}
      ),

      units: nextUnits,
    },

    nextManuscriptPlan: {
      ...(
        projectPlan
          .manuscriptPlan ||
        {}
      ),

      entries:
        nextManuscriptEntries,
    },

    nextIllustrationPlan: {
      ...(
        projectPlan
          .illustrationPlan ||
        {}
      ),

      entries:
        nextIllustrationEntries,
    },
  };
}


function manuscriptWordLayout(
  value
) {
  const normalized =
    String(value ?? "")
      .replace(/\r\n?/g, "\n")
      .trim();

  if (!normalized) {
    return {
      words: [],
      paragraphBoundaries:
        new Set(),
    };
  }

  const words = [];

  const paragraphBoundaries =
    new Set();

  normalized
    .split(/\n[ \t]*\n+/)
    .map(clean)
    .filter(Boolean)
    .forEach(
      (
        paragraph,
        paragraphIndex
      ) => {
        const paragraphWords =
          paragraph
            .split(/\s+/)
            .filter(Boolean);

        if (
          paragraphIndex > 0 &&
          paragraphWords.length
        ) {
          paragraphBoundaries.add(
            words.length
          );
        }

        words.push(
          ...paragraphWords
        );
      }
    );

  return {
    words,
    paragraphBoundaries,
  };
}

function segmentTextFromWords({
  words,
  start,
  end,
  paragraphBoundaries,
}) {
  const paragraphs = [];

  let currentWords = [];

  for (
    let index = start;
    index < end;
    index += 1
  ) {
    if (
      index > start &&
      paragraphBoundaries.has(
        index
      )
    ) {
      paragraphs.push(
        currentWords.join(" ")
      );

      currentWords = [];
    }

    currentWords.push(
      words[index]
    );
  }

  if (currentWords.length) {
    paragraphs.push(
      currentWords.join(" ")
    );
  }

  return paragraphs.join(
    "\n\n"
  );
}

function chooseBalancedBoundary({
  start,
  totalWords,
  remainingWords,
  remainingSegments,
  wordLimit,
  paragraphBoundaries,
}) {
  const minimumSize =
    Math.max(
      1,
      remainingWords -
        wordLimit *
          (
            remainingSegments -
            1
          )
    );

  const maximumSize =
    Math.min(
      wordLimit,
      remainingWords -
        (
          remainingSegments -
          1
        )
    );

  const targetSize =
    Math.ceil(
      remainingWords /
        remainingSegments
    );

  const minimumEnd =
    start + minimumSize;

  const maximumEnd =
    start + maximumSize;

  const targetEnd =
    Math.max(
      minimumEnd,
      Math.min(
        maximumEnd,
        start + targetSize
      )
    );

  const paragraphCandidates =
    Array.from(
      paragraphBoundaries
    )
      .filter(
        (boundary) =>
          boundary >= minimumEnd &&
          boundary <= maximumEnd &&
          boundary < totalWords
      )
      .sort(
        (
          first,
          second
        ) => {
          const firstDistance =
            Math.abs(
              first - targetEnd
            );

          const secondDistance =
            Math.abs(
              second - targetEnd
            );

          if (
            firstDistance !==
            secondDistance
          ) {
            return (
              firstDistance -
              secondDistance
            );
          }

          return second - first;
        }
      );

  return (
    paragraphCandidates[0] ||
    targetEnd
  );
}

function paragraphAwareSegments(
  manuscriptText,
  wordLimit
) {
  const {
    words,
    paragraphBoundaries,
  } =
    manuscriptWordLayout(
      manuscriptText
    );

  if (!words.length) {
    return [];
  }

  const segmentCount =
    Math.ceil(
      words.length /
        wordLimit
    );

  if (segmentCount <= 1) {
    return [
      segmentTextFromWords({
        words,
        start: 0,
        end: words.length,
        paragraphBoundaries,
      }),
    ];
  }

  const segments = [];

  let start = 0;

  for (
    let segmentIndex = 0;
    segmentIndex < segmentCount;
    segmentIndex += 1
  ) {
    const remainingWords =
      words.length - start;

    const remainingSegments =
      segmentCount -
      segmentIndex;

    const end =
      remainingSegments === 1
        ? words.length
        : chooseBalancedBoundary({
            start,
            totalWords:
              words.length,
            remainingWords,
            remainingSegments,
            wordLimit,
            paragraphBoundaries,
          });

    segments.push(
      segmentTextFromWords({
        words,
        start,
        end,
        paragraphBoundaries,
      })
    );

    start = end;
  }

  return segments;
}

export function suggestChildrensBookManuscriptUnitDivision({
  projectPlan = {},
  sourceUnitId = "",
  preferredUnitType = "",
  illustrationSegmentIndex,
  childrensBookAssets = [],
} = {}) {
  const normalizedSourceUnitId =
    clean(sourceUnitId);

  const units = safeArray(
    projectPlan.bookMap?.units
  );

  const sourceUnit =
    units.find(
      (unit) =>
        clean(unit?.id) ===
        normalizedSourceUnitId
    );

  if (
    !normalizedSourceUnitId ||
    !sourceUnit
  ) {
    return {
      ...failure(
        "source_unit_not_found",
        "The page or spread to divide could not be found."
      ),

      divisionNeeded: false,
    };
  }

  const manuscriptEntries =
    safeArray(
      projectPlan.manuscriptPlan
        ?.entries
    );

  const matchingEntries =
    manuscriptEntries.filter(
      (entry) =>
        clean(entry?.unitId) ===
        normalizedSourceUnitId
    );

  if (
    matchingEntries.length !== 1
  ) {
    return {
      ...failure(
        "source_manuscript_not_found",
        "Exactly one manuscript entry is required for the page or spread being divided."
      ),

      divisionNeeded: false,
    };
  }

  const sourceEntry =
    matchingEntries[0];

  const structuredSource =
    hasOwn(
      sourceEntry,
      "manuscriptContent"
    );

  const manuscriptText =
    plainTextFromManuscriptEntry(
      sourceEntry
    );

  if (
    !structuredSource &&
    !clean(manuscriptText)
  ) {
    return {
      ...failure(
        "source_manuscript_empty",
        "The manuscript unit has no text to divide."
      ),

      divisionNeeded: false,
    };
  }

  // Manuscript division now proposes physical pages.
  // Consecutive pages may be displayed together as a
  // facing-page spread, but each page owns its own text.
  const suggestedUnitType =
    "single_page";

  const wordLimit =
    MANUSCRIPT_PAGE_MAXIMUM_WORDS;

  const structuredSuggestion =
    structuredSource
      ? suggestChildrensBookStructuredManuscriptDivision({
          manuscriptContent:
            sourceEntry.manuscriptContent,
          wordLimit,
        })
      : null;

  if (
    structuredSource &&
    !structuredSuggestion.ok
  ) {
    return {
      ...failure(
        structuredSuggestion.code,
        structuredSuggestion.error
      ),

      divisionNeeded: false,
    };
  }

  const sourceWordCount =
    structuredSource
      ? structuredSuggestion
          .sourceWordCount
      : countManuscriptWords(
          manuscriptText
        );

  const structuredSegments =
    structuredSource
      ? structuredSuggestion
          .segments.map(
            (segment) =>
              segment.displayText
          )
      : null;

  const divisionNeeded =
    structuredSource
      ? structuredSegments.length > 1
      : sourceWordCount >
          wordLimit;

  if (!divisionNeeded) {
    return {
      ok: true,
      readyToApply: false,
      divisionNeeded: false,
      code:
        "division_not_needed",

      source: {
        unitId:
          normalizedSourceUnitId,
        wordCount:
          sourceWordCount,
        wordLimit,
        unit: sourceUnit,
        manuscriptEntry:
          sourceEntry,
      },

      suggestion: {
        paragraphAware: true,
        preferredUnitType:
          suggestedUnitType,

        physicalPageModel:
          true,

        preferredMinimum:
          MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS,

        preferredTarget:
          MANUSCRIPT_PAGE_TARGET_WORDS,

        wordLimit,
        segments:
          structuredSource
            ? structuredSegments
            : [
                manuscriptText,
              ],
        unitTypes: [
          suggestedUnitType,
        ],
        suggestedSegmentCount:
          1,

        sourceContentMode:
          structuredSource
            ? "structured"
            : "legacy_text",
      },
    };
  }

  const suggestedSegments =
    structuredSource
      ? structuredSegments
      : paragraphAwareSegments(
          manuscriptText,
          wordLimit
        );

  const suggestedUnitTypes =
    suggestedSegments.map(
      () =>
        suggestedUnitType
    );

  const proposal =
    buildChildrensBookManuscriptUnitDivisionProposal({
      projectPlan,
      sourceUnitId:
        normalizedSourceUnitId,
      segments:
        suggestedSegments,
      unitTypes:
        suggestedUnitTypes,
      illustrationSegmentIndex,
      childrensBookAssets,
    });

  return {
    ...proposal,
    divisionNeeded: true,

    suggestion: {
      paragraphAware: true,

      preferredUnitType:
        suggestedUnitType,

      physicalPageModel:
        true,

      preferredMinimum:
        MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS,

      preferredTarget:
        MANUSCRIPT_PAGE_TARGET_WORDS,

      wordLimit,

      segments:
        suggestedSegments,

      unitTypes:
        suggestedUnitTypes,

      suggestedSegmentCount:
        suggestedSegments.length,

      sourceWordCount,

      sourceContentMode:
        structuredSource
          ? "structured"
          : "legacy_text",
    },
  };
}
